import * as React from 'react'
import { StyleSheet, View, ViewStyle } from 'react-native'
import { neutralGray } from '@themes/Colors'

interface BaseProps {
  isCircle?: boolean
}

interface CircleProps extends BaseProps {
  isCircle: true
  size: number
}

interface RectProps extends BaseProps {
  isCircle?: false
  width: ViewStyle['width']
  height: ViewStyle['height']
}

type Props = CircleProps | RectProps

export function Skeleton(props: Props) {
  return props.isCircle ? (
    <CircleSkeleton size={props.size} />
  ) : (
    <RectSkeleton width={props.width} height={props.height} />
  )
}

export function LoadingSkeleton({
  isLoading,
  children,
  ...props
}: React.PropsWithChildren<Props & { isLoading: boolean }>) {
  return isLoading ? <Skeleton {...props} /> : children
}

function CircleSkeleton({ size = 12 }: Pick<CircleProps, 'size'>) {
  return (
    <View
      style={[
        skeletonStyles.base,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
        },
      ]}
    />
  )
}

function RectSkeleton(props: Pick<RectProps, 'width' | 'height'>) {
  return (
    <View
      style={[
        skeletonStyles.base,
        {
          width: props.width,
          height: props.height,
        },
      ]}
    />
  )
}

export function withSkeleton<P>(
  WrappedComponent: React.FC<P>,
  skeletonProps: Props
) {
  const renderComponent = (props: P & { isLoading?: boolean }) => {
    return props.isLoading ? (
      <Skeleton {...skeletonProps} />
    ) : (
      <WrappedComponent {...props} />
    )
  }

  renderComponent.displayName = `withSkeleton(${getDisplayName(
    WrappedComponent
  )})`

  return renderComponent
}

function getDisplayName<P>(WrappedComponent: React.FC<P>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}

export const skeletonStyles = StyleSheet.create({
  base: { borderRadius: 2, backgroundColor: neutralGray },
})
